/**
 * ReimportModal - Re-import using saved provider credentials safely.
 *
 * Supports: proceed with saved creds, edit saved creds, one-time different creds.
 * Never overwrites saved credentials unless Remember is enabled and import succeeds.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { buildProviderImportStartPayload } from '../../utils/providerImportPayload';
import { providerRequiresVerificationField } from '../../config/providerCapabilities';
import { getImportStartErrorMessage } from '../../hooks/useProviderRefreshImport';
import { normalizeSavedCredentialInfo, looksLikeMaskedUsername } from '../../utils/providerCredentialDisplay';
import ProviderSavedCredentialPanel from '../import/ProviderSavedCredentialPanel';
import ImportReportBureauLogos from '../import/ImportReportBureauLogos';
import { clearProviderCredentialRevealSession } from '../../hooks/useProviderCredentialReveal';
import {
  Key,
  Shield,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  Edit3,
  Lock,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { ProcessingLoader } from './ProcessingLoader';
import { useI18n } from '../../context/I18nContext';
import { classifyImportError } from '../../utils/creditImportStatus';
import {
  buildRefreshActionState,
  buildUnknownRefreshStatusPayload,
  REFRESH_STATUS_UNAVAILABLE_MESSAGE,
} from '../../utils/providerRefreshAction';

const DEFAULT_REFRESH_ACTION = {
  action: 'check',
  primary_label: 'Proceed',
  skip_confirmation: false,
  requires_credentials: true,
  can_refresh_now: false,
};

const resolveRefreshActionState = (refreshMeta, { hasCredentials = false } = {}) => {
  try {
    if (typeof buildRefreshActionState !== 'function') {
      return DEFAULT_REFRESH_ACTION;
    }
    return buildRefreshActionState(refreshMeta, { hasCredentials })
      || DEFAULT_REFRESH_ACTION;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[ReimportModal] refresh action unavailable', error);
    }
    return DEFAULT_REFRESH_ACTION;
  }
};

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const PROVIDER_DISPLAY_NAMES = {
  identityiq: 'IdentityIQ',
  credit_hero_score: 'CreditHeroScore',
  smartcredit: 'SmartCredit',
  myscoreiq: 'MyScoreIQ',
  privacyguard: 'PrivacyGuard',
};

const PROVIDER_COLORS = {
  identityiq: '#16B67A',
  credit_hero_score: '#1e3a8a',
  smartcredit: '#3B82F6',
  myscoreiq: '#8B5CF6',
  privacyguard: '#F59E0B',
  default: '#0E7A5A',
};

const MASKED_DOTS = '••••••••';
const MASKED_SECURITY = '••••';

const inputStyle = (theme) => ({
  background: theme?.surface?.s2 || '#0D1F1A',
  border: `1px solid ${theme?.border?.b2 || '#2D4A44'}`,
  color: theme?.text?.primary || '#fff',
  caretColor: theme?.text?.primary || '#fff',
});

const SensitiveField = ({
  label,
  value,
  onChange,
  showValue,
  onToggleShow,
  placeholder,
  theme,
  required = false,
  autoComplete = 'new-password',
}) => (
  <div className="space-y-2">
    <Label style={{ color: theme?.text?.secondary || '#E5E7EB' }}>
      {label}{required ? <span style={{ color: '#ef4444' }}> *</span> : null}
    </Label>
    <div className="relative">
      <Input
        type={showValue ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="selection:bg-emerald-600/30 selection:text-inherit"
        style={{ ...inputStyle(theme), paddingRight: '40px' }}
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-3 top-1/2 -translate-y-1/2"
        style={{ color: theme?.text?.muted || '#9CA3AF' }}
        aria-label={showValue ? 'Hide' : 'Show'}
      >
        {showValue ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

const SavedCredentialRow = ({ label, value, theme }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs shrink-0" style={{ color: theme?.text?.muted || '#9CA3AF' }}>
      {label}
    </span>
    <span
      className="text-sm font-mono tracking-wider"
      style={{ color: theme?.text?.secondary || '#E5E7EB' }}
    >
      {value}
    </span>
  </div>
);

// legacy row kept for compatibility in edit flows

const ReimportModal = ({ isOpen, onClose, report, onImportComplete, theme, preferAutoStart = false }) => {
  const { t } = useI18n();
  const CR = 'creditReport';

  const provider = report?.import_provider?.toLowerCase()
    || report?.origin?.provider?.toLowerCase()
    || (report?.import_source?.includes('identityiq') ? 'identityiq' : null);

  const [step, setStep] = useState('confirm');
  const [savedCredentials, setSavedCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showSecurityAnswer, setShowSecurityAnswer] = useState(false);
  const [importId, setImportId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorState, setErrorState] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [rememberForFuture, setRememberForFuture] = useState(true);
  const [refreshMeta, setRefreshMeta] = useState(null);
  const [refreshStatusUnavailable, setRefreshStatusUnavailable] = useState(false);
  const [canRevealCredentials, setCanRevealCredentials] = useState(true);
  const [revealBlockedReason, setRevealBlockedReason] = useState(null);
  const autoStartedRef = useRef(false);

  const [formCredentials, setFormCredentials] = useState({
    providerUsername: '',
    providerPassword: '',
    providerSecurityAnswer: '',
  });

  const providerDisplayName = PROVIDER_DISPLAY_NAMES[provider] || provider || 'Credit Monitoring Service';
  const providerColor = PROVIDER_COLORS[provider] || PROVIDER_COLORS.default;
  const bureauLogoRow = (
    <ImportReportBureauLogos isDark={Boolean(theme?.isDark)} className="mt-2 mb-1" />
  );
  const credInfo = normalizeSavedCredentialInfo(savedCredentials);
  const hasSaved = Boolean(credInfo);

  const refreshAction = useMemo(
    () => resolveRefreshActionState(refreshMeta, { hasCredentials: hasSaved }),
    [refreshMeta, hasSaved],
  );
  const primaryLabel = refreshAction?.action === 'refresh_now'
    ? t(`${CR}.refreshReport`)
    : t(`${CR}.proceed`);

  const resetForm = () => {
    setFormCredentials({ providerUsername: '', providerPassword: '', providerSecurityAnswer: '' });
    setShowPassword(false);
    setShowSecurityAnswer(false);
    setRememberForFuture(true);
  };

  const fetchSavedCredentials = useCallback(async () => {
    if (!provider) return null;
    const token = localStorage.getItem('fundelite_token');
    const response = await axios.get(`${API}/credit-report/credentials`, {
      params: { provider },
      headers: { Authorization: `Bearer ${token}` },
    });

    setCanRevealCredentials(response.data?.canRevealCredentials !== false);
    setRevealBlockedReason(response.data?.revealBlockedReason || null);

    let cred = null;
    if (response.data?.credentials?.length > 0) {
      cred = response.data.credentials.find((c) => c.provider === provider) || response.data.credentials[0];
    } else if (response.data?.provider === provider) {
      cred = response.data;
    }
    setSavedCredentials(cred);
    return cred;
  }, [provider]);

  useEffect(() => {
    if (!isOpen || !provider) return;
    const authToken = localStorage.getItem('fundelite_token');
    setLoading(true);
    Promise.all([
      fetchSavedCredentials(),
      axios.get(`${API}/credit-report/refresh-status/${provider}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      }).catch(() => null),
    ])
      .then(([cred, refreshRes]) => {
        const credsExist = Boolean(cred);
        if (refreshRes?.data?.success) {
          setRefreshMeta(refreshRes.data);
          setRefreshStatusUnavailable(false);
        } else {
          setRefreshMeta(buildUnknownRefreshStatusPayload({
            providerKey: provider,
            hasCredentials: credsExist,
          }));
          setRefreshStatusUnavailable(true);
        }
      })
      .catch(() => {
        setSavedCredentials(null);
        setRefreshMeta(buildUnknownRefreshStatusPayload({ providerKey: provider }));
        setRefreshStatusUnavailable(true);
      })
      .finally(() => setLoading(false));
  }, [isOpen, provider, fetchSavedCredentials]);

  useEffect(() => {
    if (!isOpen) {
      setStep('confirm');
      setImportId(null);
      setErrorMessage('');
      setErrorState(null);
      resetForm();
      setRefreshMeta(null);
      setRefreshStatusUnavailable(false);
      clearProviderCredentialRevealSession();
      autoStartedRef.current = false;
    }
  }, [isOpen]);

  const startImport = async (payload) => {
    const authToken = localStorage.getItem('fundelite_token');
    const response = await axios.post(
      `${API}/credit-report/import/start`,
      payload,
      { headers: { Authorization: `Bearer ${authToken}` } },
    );
    if (!response.data?.import_id) throw new Error('No import ID returned');
    setImportId(response.data.import_id);
    setStep('importing');
  };

  const handleProceedWithSaved = useCallback(async () => {
    if (!provider) {
      toast.error('Unable to determine provider');
      return;
    }
    setStep('importing');
    try {
      await startImport(buildProviderImportStartPayload({
        provider,
        useSavedCredentials: true,
        saveCredentials: false,
      }));
    } catch (error) {
      setErrorMessage(getImportStartErrorMessage(error));
      setStep('error');
    }
  }, [provider]);

  useEffect(() => {
    if (!isOpen || loading || autoStartedRef.current) return;
    if (
      refreshAction?.skip_confirmation
      && refreshAction?.action === 'refresh_now'
      && hasSaved
    ) {
      autoStartedRef.current = true;
      handleProceedWithSaved();
    } else if (preferAutoStart && refreshAction?.action === 'refresh_now' && !hasSaved && step === 'confirm') {
      setStep('no_saved');
    } else if (refreshAction?.action === 'connect' && !hasSaved && step === 'confirm') {
      setStep('no_saved');
    }
  }, [isOpen, loading, hasSaved, refreshAction, handleProceedWithSaved, step, preferAutoStart]);

  const handleEditImport = async () => {
    const { providerUsername, providerPassword, providerSecurityAnswer } = formCredentials;
    const needsVerification = providerRequiresVerificationField(provider);
    if (!hasSaved) {
      if (!providerUsername || !providerPassword || (needsVerification && !providerSecurityAnswer)) {
        toast.error('Please complete all provider credential fields');
        return;
      }
    } else if (!providerUsername && !providerPassword && !providerSecurityAnswer) {
      toast.error('Change at least one field or use Proceed with saved credentials');
      return;
    }

    if (providerUsername && looksLikeMaskedUsername(providerUsername)) {
      toast.error('Enter your full provider email/username, not the masked value');
      return;
    }

    setIsSaving(true);
    try {
      await startImport(buildProviderImportStartPayload({
        provider,
        useSavedCredentials: hasSaved,
        saveCredentials: rememberForFuture,
        oneTimeCredentials: false,
        providerUsername: hasSaved ? undefined : providerUsername,
        providerPassword: hasSaved ? undefined : providerPassword,
        providerSecurityAnswer: hasSaved ? undefined : providerSecurityAnswer,
        providerUsernameOverride: hasSaved ? providerUsername : undefined,
        providerPasswordOverride: hasSaved ? providerPassword : undefined,
        providerSecurityAnswerOverride: hasSaved ? providerSecurityAnswer : undefined,
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start import');
      if (!hasSaved) {
        setErrorMessage(error.response?.data?.detail || 'Failed to start import');
        setStep('error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleOneTimeImport = async () => {
    const { providerUsername, providerPassword, providerSecurityAnswer } = formCredentials;
    if (!providerUsername || !providerPassword) {
      toast.error('Provider username and password are required');
      return;
    }
    if (looksLikeMaskedUsername(providerUsername)) {
      toast.error('Enter your full provider email/username');
      return;
    }

    setIsSaving(true);
    try {
      await startImport(buildProviderImportStartPayload({
        provider,
        useSavedCredentials: false,
        saveCredentials: false,
        oneTimeCredentials: true,
        providerUsername,
        providerPassword,
        providerSecurityAnswer: providerSecurityAnswer || undefined,
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start import');
      setErrorMessage(error.response?.data?.detail || 'Failed to start import');
      setStep('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportComplete = useCallback((result) => {
    setStep('success');
    toast.success('Latest report imported successfully');
    setTimeout(() => {
      onClose();
      onImportComplete?.(result);
    }, 1500);
  }, [onClose, onImportComplete]);

  const handleImportError = useCallback((error) => {
    const classified = classifyImportError(error);
    setErrorState(classified);
    setErrorMessage(classified.message);
    setStep('error');
  }, []);

  const dialogShell = {
    background: theme?.card?.bg || '#1A2F2A',
    border: `1px solid ${theme?.card?.border || '#2D4A44'}`,
    borderRadius: '22px',
  };

  if (loading && ['confirm', 'no_saved'].includes(step)) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[480px]" style={dialogShell}>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: providerColor }} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (step === 'importing') {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}} modal>
        <DialogContent className="sm:max-w-[900px] p-0 border-0 bg-transparent" style={{ maxHeight: '90vh', overflow: 'auto' }} hideClose>
          <DialogTitle className="sr-only">Importing Credit Report</DialogTitle>
          <ProcessingLoader isProcessing importId={importId} onComplete={handleImportComplete} onError={handleImportError} />
        </DialogContent>
      </Dialog>
    );
  }

  if (step === 'success') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[480px]" style={dialogShell}>
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2" style={{ color: theme?.text?.primary || '#fff' }}>Import Complete</h3>
            <p className="text-sm" style={{ color: theme?.text?.muted || '#9CA3AF' }}>Your latest credit report has been imported successfully.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (step === 'error') {
    const showEditCredentials = Boolean(errorState?.showEditCredentials);
    const showTryAgain = Boolean(errorState?.showTryAgain);
    const errorTitle = errorState?.title || 'Import Failed';

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[480px]" style={dialogShell}>
          <DialogHeader>
            <DialogTitle style={{ color: theme?.text?.primary || '#fff' }}>{errorTitle}</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <p className="text-sm mb-6" style={{ color: theme?.text?.muted || '#9CA3AF' }}>{errorMessage}</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                {showEditCredentials ? 'Cancel' : 'Close'}
              </Button>
              {showTryAgain ? (
                <Button
                  onClick={() => {
                    setErrorState(null);
                    setErrorMessage('');
                    handleProceedWithSaved();
                  }}
                  className="flex-1"
                  style={{ background: providerColor, color: '#fff' }}
                >
                  Try Again
                </Button>
              ) : null}
              {showEditCredentials ? (
                <Button
                  onClick={() => setStep(hasSaved ? 'edit' : 'no_saved')}
                  className="flex-1"
                  style={{ background: providerColor, color: '#fff' }}
                >
                  {t(`${CR}.editCredentials`)}
                </Button>
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const renderCredentialForm = ({ mode }) => (
    <div className="space-y-4 py-2">
      <p className="text-xs" style={{ color: theme?.text?.muted || '#9CA3AF' }}>
        {t(`${CR}.providerPasswordHint`)}
      </p>
      <div className="space-y-2">
        <Label style={{ color: theme?.text?.secondary || '#E5E7EB' }}>Email / Username</Label>
        <Input
          type="text"
          value={formCredentials.providerUsername}
          onChange={(e) => setFormCredentials((p) => ({ ...p, providerUsername: e.target.value }))}
          placeholder={mode === 'edit' && credInfo ? credInfo.usernameMasked : 'Enter provider email or username'}
          autoComplete="off"
          className="selection:bg-emerald-600/30 selection:text-inherit"
          style={inputStyle(theme)}
        />
      </div>
      <SensitiveField
        label="Password"
        value={formCredentials.providerPassword}
        onChange={(e) => setFormCredentials((p) => ({ ...p, providerPassword: e.target.value }))}
        showValue={showPassword}
        onToggleShow={() => setShowPassword((v) => !v)}
        placeholder={mode === 'edit' && credInfo?.passwordSaved ? 'Leave blank to keep saved password' : 'Enter provider password'}
        theme={theme}
        required={mode === 'no_saved' || mode === 'one_time'}
      />
      {providerRequiresVerificationField(provider) && (
        <SensitiveField
          label="Last 4 of SSN or Security Answer"
          value={formCredentials.providerSecurityAnswer}
          onChange={(e) => setFormCredentials((p) => ({ ...p, providerSecurityAnswer: e.target.value }))}
          showValue={showSecurityAnswer}
          onToggleShow={() => setShowSecurityAnswer((v) => !v)}
          placeholder={
            mode === 'edit' && credInfo?.securityAnswerSaved
              ? 'Leave blank to keep saved value'
              : 'Enter last 4 SSN digits or security answer'
          }
          theme={theme}
          required={mode === 'no_saved' || (mode === 'edit' && !credInfo?.securityAnswerSaved)}
        />
      )}
      {mode === 'edit' && (
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={rememberForFuture} onCheckedChange={(v) => setRememberForFuture(Boolean(v))} />
          <span className="text-sm" style={{ color: theme?.text?.secondary || '#E5E7EB' }}>
            {t(`${CR}.rememberForFutureImports`)}
          </span>
        </label>
      )}
      {mode === 'no_saved' && (
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={rememberForFuture} onCheckedChange={(v) => setRememberForFuture(Boolean(v))} />
          <span className="text-sm" style={{ color: theme?.text?.secondary || '#E5E7EB' }}>
            {t(`${CR}.rememberForFutureImports`)}
          </span>
        </label>
      )}
    </div>
  );

  if (step === 'edit' || step === 'no_saved' || step === 'one_time') {
    const titles = {
      edit: t(`${CR}.editCredentials`),
      no_saved: t(`${CR}.enterCredentials`),
      one_time: t(`${CR}.useDifferentCredentialsOnce`),
    };
    const mode = step === 'one_time' ? 'one_time' : step === 'no_saved' ? 'no_saved' : 'edit';

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[480px]" style={dialogShell}>
          <DialogHeader>
            <DialogTitle style={{ color: theme?.text?.primary || '#fff' }}>{titles[step]}</DialogTitle>
          </DialogHeader>
          {bureauLogoRow}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-2" style={{ background: `${providerColor}15`, border: `1px solid ${providerColor}30` }}>
            <Lock size={14} style={{ color: providerColor }} />
            <span className="text-sm font-medium" style={{ color: providerColor }}>{providerDisplayName}</span>
          </div>
          {renderCredentialForm({ mode })}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setStep(hasSaved ? 'confirm' : 'no_saved')} className="flex-1" style={inputStyle(theme)}>
              Back
            </Button>
            <Button
              onClick={step === 'one_time' ? handleOneTimeImport : handleEditImport}
              disabled={isSaving}
              className="flex-1"
              style={{ background: providerColor, color: '#fff', opacity: isSaving ? 0.6 : 1 }}
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {step === 'one_time' ? t(`${CR}.proceed`) : hasSaved ? t(`${CR}.saveAndImport`) : t(`${CR}.enterCredentials`)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]" style={dialogShell} data-testid="reimport-modal">
        <DialogHeader>
          <DialogTitle style={{ color: theme?.text?.primary || '#fff' }}>
            {hasSaved ? t(`${CR}.reimportReport`) : t(`${CR}.enterCredentials`)}
          </DialogTitle>
        </DialogHeader>
        {bureauLogoRow}

        <div className="space-y-5 py-2">
          <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: `${providerColor}08`, border: `1px solid ${providerColor}20` }}>
            <Key size={20} style={{ color: providerColor }} />
            <div>
              <p className="font-bold" style={{ color: theme?.text?.primary || '#fff' }}>{providerDisplayName}</p>
              <p className="text-sm" style={{ color: theme?.text?.muted || '#9CA3AF' }}>{t(`${CR}.credentialImportLabel`)}</p>
            </div>
          </div>

          {hasSaved ? (
            <ProviderSavedCredentialPanel
              provider={provider}
              credInfo={credInfo}
              canReveal={canRevealCredentials}
              revealBlockedReason={revealBlockedReason}
              theme={theme}
            />
          ) : (
            <div className="p-4 rounded-xl" style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-red-500" />
                <span className="text-sm font-medium" style={{ color: theme?.text?.primary || '#fff' }}>
                  {t(`${CR}.noSavedCredentialsFound`)}
                </span>
              </div>
            </div>
          )}

          {refreshStatusUnavailable && (
            <p
              className="text-xs text-center"
              style={{ color: theme?.text?.muted || '#9CA3AF' }}
              data-testid="reimport-refresh-status-unavailable"
            >
              {REFRESH_STATUS_UNAVAILABLE_MESSAGE}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-1.5">
              <Shield size={12} className="text-emerald-500" />
              <span className="text-xs" style={{ color: theme?.text?.muted || '#9CA3AF' }}>Encrypted session</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          {hasSaved ? (
            <>
              <Button onClick={handleProceedWithSaved} style={{ background: providerColor, color: '#fff' }} data-testid="reimport-proceed-btn">
                <RefreshCw size={14} className="mr-2" />
                {primaryLabel}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { resetForm(); setStep('edit'); }} className="flex-1" style={inputStyle(theme)}>
                  <Edit3 size={14} className="mr-1" />
                  {t(`${CR}.editCredentials`)}
                </Button>
                <Button variant="outline" onClick={() => { resetForm(); setStep('one_time'); }} className="flex-1" style={inputStyle(theme)}>
                  {t(`${CR}.useDifferentCredentialsOnce`)}
                </Button>
              </div>
            </>
          ) : (
            <Button onClick={() => { resetForm(); setStep('no_saved'); }} style={{ background: providerColor, color: '#fff' }}>
              <Key size={14} className="mr-2" />
              {t(`${CR}.enterCredentials`)}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} style={{ color: theme?.text?.muted || '#9CA3AF' }}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ReimportModal;
